import { PACKAGE } from "../../../../constants";
import {
  CalculatorServiceLeasingNewEnum,
  CalculatorServiceRentNewEnum
} from "../types/packagesEnums";
import type { RentAdditionServiceConfig } from "../types/calculatorData";

export type AdditionalPackagesContext = "rentNew" | "leasingNew" | "rentAfterlease" | "leasingAfterlease";

export enum PackageKind {
  Insurance = "insurance",
  Assistance = "assistance",
  PetrolCard = "petrolCard",
  Tyres = "tyres",
  TechnicalService = "technicalService",
  ManagementBasic = "managementBasic",
  ManagementComfort = "managementComfort",
  ManagementPrestigeNonAso = "managementPrestigeNonAso",
  ManagementPrestige = "managementPrestige"
}

/** Shared by leasingNew, rentAfterlease, and leasingAfterlease — identical service IDs. */
const STANDARD_SERVICE_TO_KIND: Record<number, PackageKind> = {
  [CalculatorServiceLeasingNewEnum.TechnicalService]: PackageKind.TechnicalService,
  [CalculatorServiceLeasingNewEnum.PetrolCard]: PackageKind.PetrolCard,
  [CalculatorServiceLeasingNewEnum.TiresService]: PackageKind.Tyres,
  [CalculatorServiceLeasingNewEnum.Assistance]: PackageKind.Assistance,
  [CalculatorServiceLeasingNewEnum.Insurance]: PackageKind.Insurance
};

const RENT_NEW_SERVICE_TO_KIND: Record<number, PackageKind> = {
  [CalculatorServiceRentNewEnum.Insurance]: PackageKind.Insurance,
  [CalculatorServiceRentNewEnum.Assistance]: PackageKind.Assistance,
  [CalculatorServiceRentNewEnum.ManagementBasic]: PackageKind.ManagementBasic,
  [CalculatorServiceRentNewEnum.PetrolCard]: PackageKind.PetrolCard,
  [CalculatorServiceRentNewEnum.Tires]: PackageKind.Tyres,
  [CalculatorServiceRentNewEnum.ManagementComfort]: PackageKind.ManagementComfort,
  [CalculatorServiceRentNewEnum.ManagementPrestigeNonAso]: PackageKind.ManagementPrestigeNonAso,
  [CalculatorServiceRentNewEnum.ManagementPrestige]: PackageKind.ManagementPrestige
};

const KIND_TO_PACKAGE_VALUE: Record<PackageKind, number> = {
  [PackageKind.Insurance]: PACKAGE.INSURANCE,
  [PackageKind.Assistance]: PACKAGE.ASSISTANCE,
  [PackageKind.PetrolCard]: PACKAGE.PETROL_CARD,
  [PackageKind.Tyres]: PACKAGE.TYRES,
  [PackageKind.TechnicalService]: PACKAGE.SERVICE_COORDINATION,
  [PackageKind.ManagementBasic]: PACKAGE.BASIC_SERVICE,
  [PackageKind.ManagementComfort]: PACKAGE.COMFORT_SERVICE,
  [PackageKind.ManagementPrestigeNonAso]: PACKAGE.PRESTIGE_SERVICE,
  [PackageKind.ManagementPrestige]: PACKAGE.PRESTIGE_ASO_SERVICE
};

const STANDARD_KIND_TO_SERVICE: Partial<Record<PackageKind, number>> = {
  [PackageKind.Insurance]: CalculatorServiceLeasingNewEnum.Insurance,
  [PackageKind.Assistance]: CalculatorServiceLeasingNewEnum.Assistance,
  [PackageKind.TechnicalService]: CalculatorServiceLeasingNewEnum.TechnicalService,
  [PackageKind.PetrolCard]: CalculatorServiceLeasingNewEnum.PetrolCard,
  [PackageKind.Tyres]: CalculatorServiceLeasingNewEnum.TiresService
};

const RENT_NEW_KIND_TO_SERVICE: Partial<Record<PackageKind, number>> = {
  [PackageKind.Insurance]: CalculatorServiceRentNewEnum.Insurance,
  [PackageKind.Assistance]: CalculatorServiceRentNewEnum.Assistance,
  [PackageKind.ManagementBasic]: CalculatorServiceRentNewEnum.ManagementBasic,
  [PackageKind.PetrolCard]: CalculatorServiceRentNewEnum.PetrolCard,
  [PackageKind.Tyres]: CalculatorServiceRentNewEnum.Tires,
  [PackageKind.ManagementComfort]: CalculatorServiceRentNewEnum.ManagementComfort,
  [PackageKind.ManagementPrestigeNonAso]: CalculatorServiceRentNewEnum.ManagementPrestigeNonAso,
  [PackageKind.ManagementPrestige]: CalculatorServiceRentNewEnum.ManagementPrestige
};

export function resolveAdditionalPackagesContext(pageType: string, activeTab: number): AdditionalPackagesContext {
  if (pageType === "afterLease") {
    return activeTab === 1 ? "rentAfterlease" : "leasingAfterlease";
  }

  return activeTab === 1 ? "rentNew" : "leasingNew";
}

export function normalizeServiceId(serviceId: number): number {
  return Number(serviceId);
}

function getServiceToKindMap(context: AdditionalPackagesContext): Record<number, PackageKind> {
  return context === "rentNew" ? RENT_NEW_SERVICE_TO_KIND : STANDARD_SERVICE_TO_KIND;
}

function getKindToServiceMap(context: AdditionalPackagesContext): Partial<Record<PackageKind, number>> {
  return context === "rentNew" ? RENT_NEW_KIND_TO_SERVICE : STANDARD_KIND_TO_SERVICE;
}

export function getPackageKind(context: AdditionalPackagesContext, serviceId: number): PackageKind | null {
  const map = getServiceToKindMap(context);
  return map[normalizeServiceId(serviceId)] ?? null;
}

export function getPackageValueForKind(kind: PackageKind): number {
  return KIND_TO_PACKAGE_VALUE[kind];
}

export function getPackageValue(context: AdditionalPackagesContext, serviceId: number): number | null {
  const kind = getPackageKind(context, serviceId);
  return kind ? getPackageValueForKind(kind) : null;
}

export function getServiceId(context: AdditionalPackagesContext, packageValue: number): number | null {
  const kind = getPackageKindFromValue(packageValue);
  if (!kind) {
    return null;
  }

  const map = getKindToServiceMap(context);
  return map[kind] ?? null;
}

export function getPackageKindFromValue(packageValue: number): PackageKind | null {
  const entry = Object.entries(KIND_TO_PACKAGE_VALUE).find(([, value]) => value === packageValue);
  return entry ? (entry[0] as PackageKind) : null;
}

export function getNewCarRentServicePackageValues(): number[] {
  return [
    getPackageValueForKind(PackageKind.ManagementBasic),
    getPackageValueForKind(PackageKind.ManagementComfort),
    getPackageValueForKind(PackageKind.ManagementPrestigeNonAso),
    getPackageValueForKind(PackageKind.ManagementPrestige)
  ];
}

export function findConfigForPackageValue(
  context: AdditionalPackagesContext,
  configuration: RentAdditionServiceConfig[],
  packageValue: number
): RentAdditionServiceConfig | undefined {
  const serviceId = getServiceId(context, packageValue);
  if (serviceId == null) {
    return undefined;
  }

  return configuration.find((configItem) => normalizeServiceId(configItem.ServiceId) === serviceId);
}

export function isAfterleaseContext(context: AdditionalPackagesContext): boolean {
  return context === "rentAfterlease" || context === "leasingAfterlease";
}

export function usesUsedCalcTooltips(context: AdditionalPackagesContext): boolean {
  return isAfterleaseContext(context) || context === "leasingNew";
}
